// ─── RBAC modules & actions ──────────────────────────────────────────────────
// One row per area of the app the sidebar exposes (Aug 2026 refresh: Wholesale,
// Inventory, Raw Materials, Locations, Devices, Downloads and Applications were
// missing, and "Labels" is branded Production). Module ids are what
// usePermissions().can(moduleId, action) and the sidebar's `module:` keys read,
// so renaming an id here means renaming it there too.

export const RBAC_MODULES = [
    // General
    { id: 'dashboard',       label: 'Dashboard',        actions: ['view'] },
    { id: 'command_center',  label: 'Command Center',   actions: ['view', 'approve', 'export'] },
    { id: 'sales',           label: 'Sales',            actions: ['view', 'export'] },
    { id: 'labels',          label: 'Production',       actions: ['view', 'print', 'export'] },
    // Stores
    { id: 'locations',       label: 'Locations',        actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'staffs',          label: 'Staffs',           actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'inventory',       label: 'Inventory',        actions: ['view', 'create', 'edit', 'export'] },
    { id: 'label_presets',   label: 'Label Presets',    actions: ['view', 'create', 'edit', 'delete'] },
    // Purchasing
    { id: 'purchase_orders', label: 'Purchase Orders',  actions: ['view', 'create', 'edit', 'delete', 'approve', 'export'] },
    { id: 'vendors',         label: 'Vendors',          actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'raw_materials',   label: 'Raw Materials',    actions: ['view', 'create', 'edit', 'delete'] },
    // Receiving
    { id: 'products',        label: 'Products',         actions: ['view', 'create', 'edit', 'delete', 'export'] },
    { id: 'label_formats',   label: 'Barcode Formats',  actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'storage',         label: 'Storage',          actions: ['view', 'create', 'edit', 'delete'] },
    // Wholesale (customers, applicants, orders, invoices, pricing, aging)
    { id: 'wholesale',       label: 'Wholesale',        actions: ['view', 'create', 'edit', 'delete', 'approve', 'export'] },
    // Apps & devices
    { id: 'devices',         label: 'Devices',          actions: ['view', 'edit'] },
    { id: 'downloads',       label: 'Downloads',        actions: ['view'] },
    { id: 'applications',    label: 'Applications',     actions: ['view', 'edit', 'approve'] },
    // Workspace
    { id: 'collaboration',   label: 'Collaboration',    actions: ['view', 'invite', 'edit', 'delete', 'manage_roles'] },
    { id: 'settings',        label: 'Settings',         actions: ['view', 'edit'] },
];

export const ALL_ACTIONS = [
    'view', 'create', 'edit', 'delete', 'export', 'print', 'approve', 'invite', 'manage_roles',
];

export const ACTION_LABELS = {
    view:         'View',
    create:       'Create',
    edit:         'Edit',
    delete:       'Delete',
    export:       'Export',
    print:        'Print',
    approve:      'Approve',
    invite:       'Invite',
    manage_roles: 'Roles',
};

// Older records were saved under these ids; they map onto today's modules so a
// collaborator invited before the refresh keeps the access they were given.
const LEGACY_MODULE_IDS = {
    invoices: 'wholesale',
};

// ─── Per-role default grants ──────────────────────────────────────────────────

const ALL = (mod) => [...mod.actions];
const VIEW = ['view'];

// Everything a module offers, for the roles that get all of it.
const everything = () => Object.fromEntries(RBAC_MODULES.map(m => [m.id, ALL(m)]));

const ROLE_DEFAULTS = {
    admin: everything(),

    // Runs the business day to day: everything except who's on the team and
    // the workspace's own settings.
    manager: {
        ...everything(),
        collaboration: ['view', 'invite'],
        settings:      VIEW,
    },

    // Read-only across the app; no team management, no settings.
    viewer: {
        ...Object.fromEntries(RBAC_MODULES.map(m => [m.id, VIEW])),
        collaboration: [],
        settings:      [],
    },

    // Register + label printing: what a cashier needs on the floor.
    cashier: {
        dashboard:     VIEW,
        sales:         VIEW,
        labels:        ['view', 'print'],
        products:      VIEW,
        label_presets: VIEW,
        devices:       VIEW,
        downloads:     VIEW,
    },

    // Receivers + back-of-house: inventory-only. They touch stock, storage,
    // raw materials, purchase orders (to scan deliveries against) and vendors
    // (to look up who shipped a box). Everything else is hidden.
    warehouse: {
        dashboard:       VIEW,
        locations:       VIEW,
        inventory:       ['view', 'create', 'edit', 'export'],
        purchase_orders: ['view', 'edit'],
        vendors:         VIEW,
        raw_materials:   ['view', 'create', 'edit'],
        products:        ['view', 'create', 'edit'],
        storage:         ['view', 'create', 'edit'],
        devices:         VIEW,
        downloads:       VIEW,
    },

    // Wholesale desk: customers, applicants, orders, invoices and pricing, plus
    // read access to what they sell and how it's moving.
    wholesale: {
        dashboard: VIEW,
        sales:     VIEW,
        products:  VIEW,
        inventory: VIEW,
        wholesale: ['view', 'create', 'edit', 'delete', 'approve', 'export'],
        downloads: VIEW,
    },

    // Books: read + export the money views, approve nothing.
    accountant: {
        dashboard:       VIEW,
        command_center:  ['view', 'export'],
        sales:           ['view', 'export'],
        purchase_orders: ['view', 'export'],
        vendors:         VIEW,
        wholesale:       ['view', 'export'],
        products:        VIEW,
    },
};

// The preset ids, in the order the picker shows them (Custom is the catch-all).
export const PRESET_ROLE_IDS = ['admin', 'manager', 'viewer', 'cashier', 'warehouse', 'wholesale', 'accountant'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Build a full permissions object from a preset role id */
export function buildPermissions(roleId) {
    const defaults = ROLE_DEFAULTS[roleId] || {};
    const perms = {};
    RBAC_MODULES.forEach(mod => {
        perms[mod.id] = {};
        mod.actions.forEach(action => {
            perms[mod.id][action] = (defaults[mod.id] || []).includes(action);
        });
    });
    return perms;
}

/**
 * Bring a stored permissions object up to today's module list: legacy ids are
 * renamed, modules it never heard of are added (false), stray keys dropped.
 * Returns null for a missing/empty input so callers can fall back to a role.
 */
export function normalizePermissions(stored) {
    if (!stored || typeof stored !== 'object' || Object.keys(stored).length === 0) return null;
    const perms = {};
    RBAC_MODULES.forEach(mod => {
        const legacyId = Object.keys(LEGACY_MODULE_IDS).find(k => LEGACY_MODULE_IDS[k] === mod.id);
        const source = stored[mod.id] || (legacyId ? stored[legacyId] : null) || {};
        perms[mod.id] = {};
        mod.actions.forEach(action => { perms[mod.id][action] = !!source[action]; });
    });
    return perms;
}

/** Detect if a permissions object matches a preset role exactly */
export function detectRole(perms) {
    for (const roleId of PRESET_ROLE_IDS) {
        const candidate = buildPermissions(roleId);
        const match = RBAC_MODULES.every(mod =>
            mod.actions.every(action =>
                (perms[mod.id]?.[action] ?? false) === candidate[mod.id][action]
            )
        );
        if (match) return roleId;
    }
    return 'custom';
}

export const PRESET_ROLES = [
    { id: 'admin',      label: 'Admin',      description: 'Full access to every module' },
    { id: 'manager',    label: 'Manager',    description: 'Everything except team management and settings' },
    { id: 'viewer',     label: 'Viewer',     description: 'Read-only access across the app' },
    { id: 'cashier',    label: 'Cashier',    description: 'Register, label printing and product lookup' },
    { id: 'warehouse',  label: 'Warehouse',  description: 'Inventory, storage, raw materials, purchase orders and vendors' },
    { id: 'wholesale',  label: 'Wholesale',  description: 'Wholesale customers, orders, invoices and pricing' },
    { id: 'accountant', label: 'Accountant', description: 'Read and export the money views: Command Center, sales, purchasing, wholesale' },
    { id: 'custom',     label: 'Custom',     description: 'Manually configured permissions' },
];
